/** @type {Record.<string, string>} */
const delimiterCache = {}

/** @type {Readonly.<Record.<string, RegExp>>} */
const dateFormatRegexMap = {
    'dd.MM.yyyy': /^\d{2}\.\d{2}\.\d{4}$/,
    'MM/dd/yyyy': /^\d{2}\/\d{2}\/\d{4}$/,
    'dd/MM/yyyy': /^\d{2}\/\d{2}\/\d{4}$/,
    'yyyy. MM. dd.': /^\d{4}\. \d{2}\. \d{2}\.* *$/,
    'dd-MM-yyyy': /^\d{2}-\d{2}-\d{4}$/
}

/**
 * Validate a date string coming from / to the database to check if it is in the correct format.
 * Dates shown in frontend to the user should be localized and should not be validated with this function.
 * This function validates only date strings coming from / meant for the database, to catch any wrongly formatted dates.
 * @param date {string} Input date.
 * @returns {true} If date is in US American date format (MM/dd/yyyy).
 * @throws {Error} If date is NOT in US American date format (MM/dd/yyyy).
 */
export function validateDatabaseDateFormat(date) {
    /* https://stackoverflow.com/a/60944677/1328511 */
    switch (true) {
        case !date:
        case date.length != 10:
        case /^\d{2}\/\d{2}\/\d{4}$/.test(date) == false:
        case +(date.substring(0, 2)) > 12:
            throw new Error('Date is not in US American date format! (MM/dd/yyyy) - got ' + date)
    }
    return true
}

/**
 * Check a date string in the frontend if it is in the correct format and valid.
 * @param date {string} Input date.
 * @param format {string} Format that the date should be in, using "MM", "dd" and "yyyy" as placeholders.
 * @returns {boolean} True if date is exactly in the provided format, false otherwise
 */
export function validateDateFormat(date, format = Laravel.activeDateformat) {
    date = date.trim()
    /* https://stackoverflow.com/a/60944677/1328511 */
    switch (true) {
        case !date:
        case date.length != format.length:
        case dateFormatRegexMap[format].test(date) == false:
            return false
        default:
            return true
    }
}

/**
 * Validate an ISO string if it contains day and month values that are both not "00"
 * @param date {DateISO} Input date in ISO 8601 format (YYYY-MM-DD).
 * @returns {boolean} True if date contains day and month values that are both not "00", false otherwise
 */
export function validateISODayMonth(date) {
    let isDateValid = true

    // Check the MM (month) part of the string
    if (date.substring(5, 7) === '00') isDateValid = false
    // Check the DD (day) part of the string
    if (date.substring(8, 10) === '00') isDateValid = false

    return isDateValid
}

/**
 * Format date into the user's local date format.
 * @param date {DateUS} Input date in US American date format (MM/dd/yyyy).
 * @param format {string} Desired output format using "MM", "dd" and "yyyy" as placeholders.
 * @returns {string|null} Date formatted in desired format, or null if 'date' is null/empty.
 */
export function localizeDate(date, format = Laravel.activeDateformat) {

    if (!date) return null
    //validateDatabaseDateFormat(date)
    if (!format) {
        format = Laravel.activeDateformat;
    }

    if (format == 'MM/dd/yyyy') return date

    const [month, day, year] = date.split('/')

    return format
        .replace('MM', month)
        .replace('dd', day)
        .replace('yyyy', year)
        // Future proofing. Using a function ensures 'year.substring' is not calculated unless "yy" is actually found
        .replace('yy', () => year.substring(2))
        .trimEnd()
}

/**
 * Format date into US American date format (MM/dd/yyyy). Date should be validated with 'validateDateFormat' beforehand.
 * @param date {string} Input date in localized date format, should be validated with 'validateDateFormat' beforehand.
 * @param format {string} Localized date format of the input string, using "MM", "dd" and "yyyy" as placeholders.
 * @returns {DateUS|null} Date formatted in US American date format (MM/dd/yyyy), or null if 'date' is null/empty.
 */
export function unlocalizeDate(date, format = Laravel.activeDateformat) {

    if (!date) return null
    if (!format) throw new Error('Parameter \'format\' is required.')

    date = date.trim()

    if (format == 'MM/dd/yyyy') return date

    let delimiter = delimiterCache[format]
    if (!delimiter) {
        delimiter = /[^dmy]+/i.exec(format)[0]
        delimiterCache[format] = delimiter
    }

    /* Currently only the Hungarian format has ". " as  a delimiter, but their dates don't end with a space.
    * This is a special case and is handled here.
    * If any language is added to EWI in the future which also has a delimiter longer than 1,
    * then this code should be updated to account for that. */
    if (delimiter.length == 2) {
        date += ' '
        format += ' '
    }

    const dateParts = date.split(delimiter)

    let year, month, day

    format.split(delimiter).forEach((part, index) => {
        switch (part[0]) {
            case 'y':
                year = dateParts[index]
                break
            case 'M':
                month = dateParts[index]
                break
            case 'd':
                day = dateParts[index]
                break
        }
    })

    // If format was given with "yy" for years, expand the year value back to full length
    if (year.length == 2) {
        year = `20${year}`
    }

    return `${month}/${day}/${year}`
}

/**
 * Helper function to unify frontend datetime-printing - currently using Laravel.activeLocale
 * later maybe to be changed to real browser locale-string
 * @param DateTime any valid input to "new Date()" constructor
 * @returns {string} in localized, to be printed format
 */
export function FormatDateTimeEntryToLocaleString(DateTime) {
    return FormatJsDateTimeToLocaleString(new Date(DateTime));
}

/**
 * Helper function to unify frontend datetime-printing - currently using Laravel.activeLocale
 * later maybe to be changed to real browser locale-string
 * @param jsDate JS Date object to be formatted
 * @returns {string} in localized, to be printed format
 */
export function FormatJsDateTimeToLocaleString(jsDate) {
    return jsDate.toLocaleString(Laravel.activeLocale)
}


/**
 * Convert (validity) date IN UTC (but still localized, from the backend) into JS Date.
 * @param validityDate {string} Input validity date in localized date format with timestamp at the end (HH:mm:ss).
 * @param format {string} Localized date format of the input string, using "MM", "dd" and "yyyy" as placeholders. Only for the date part, the timestamp part is not localized.
 * @returns {Date} JS Date, or null if 'validityDate' is null/empty.
 */
export function validityDateUTCToJsDate(validityDate, format = Laravel.activeDateformat) {

    if(!validityDate) return null;

    validityDate = validityDate.trim();

    const timestampOffset = validityDate.length - 8;
    const timestamp = validityDate.substring(timestampOffset);

    const dateUS = unlocalizeDate(validityDate.substring(0, timestampOffset), format);
    const date = new Date(dateUS+ ' 00:00Z');

    const [hours, minutes, seconds] = timestamp.split(':');
    date.setUTCHours(+hours);
    date.setUTCMinutes(+minutes);
    date.setUTCSeconds(+seconds);

    return date;
}


/**
 * Convert validity date (localized, from the backend) into JS Date.
 * @param validityDate {string} Input validity date in localized date format with timestamp at the end (HH:mm:ss).
 * @param format {string} Localized date format of the input string, using "MM", "dd" and "yyyy" as placeholders. Only for the date part, the timestamp part is not localized.
 * @returns {Date} JS Date, or null if 'validityDate' is null/empty.
 */
export function validityDateToJsDate(validityDate, format = Laravel.activeDateformat) {

    if(!validityDate) return null;

    validityDate = validityDate.trim();

    const timestampOffset = validityDate.length - 8;
    const timestamp = validityDate.substring(timestampOffset);

    const dateUS = unlocalizeDate(validityDate.substring(0, timestampOffset), format);
    const date = new Date(dateUS);

    const [hours, minutes, seconds] = timestamp.split(':');
    date.setHours(+hours);
    date.setMinutes(+minutes);
    date.setSeconds(+seconds);

    return date;
}

/**
 * Format US American date (MM/dd/yyyy) into ISO 8601 format (YYYY-MM-DD).
 * @param date {DateUS} Input date in US American date format (MM/dd/yyyy).
 * @returns {DateISO|null} Date formatted in ISO 8601 format (YYYY-MM-DD), or null if 'date' is null/empty.
 */
export function dateUSToDateISO(date) {

    if (!date) return null

    const [month, day, year] = date.split('/')

    return `${year}-${month}-${day}`
}

/**
 * Format ISO 8601 date (YYYY-MM-DD) into US American format (MM/dd/yyyy).
 * @param date {DateISO} Input date in ISO 8601 date format (YYYY-MM-DD).
 * @returns {DateUS|null} Date formatted in US American format (MM/dd/yyyy), or null if 'date' is null/empty.
 */
export function dateISOToDateUS(date) {

    if (!date) return null

    const [year, month, day] = date.split('-')

    return `${month}/${day}/${year}`
}

/**
 * Return current date in US American format (MM/dd/yyyy).
 * @returns {DateUS} Current date formatted in US American format (MM/dd/yyyy).
 */
export function dateTodayUS() {
    return dateISOToDateUS(dateTodayISO());
}

/**
 * Return current date in ISO 8601 format (YYYY-MM-DD).
 * @returns {DateISO} Current date formatted in ISO 8601 format (YYYY-MM-DD).
 */
export function dateTodayISO() {
    return new Date().toISOString().substring(0, 10);
}

